import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

/**
 * Decode a JWT payload without verifying the signature. Purely used to
 * detect POS-scoped tokens (`is_pos: true`) so AuthContext skips the
 * /auth/me user lookup which would 404 for the synthetic POS "user".
 */
const decodeJwt = (token) => {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch (_e) {
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    // POS tokens are device-scoped, not user-scoped — skip /auth/me.
    const claims = decodeJwt(token);
    if (claims?.is_pos && !claims?.is_admin) {
      setUser({ id: 'pos-device', is_pos: true, is_admin: false, is_finance: false });
      setLoading(false);
      return;
    }
    fetchCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    return response.data;
  };

  const signup = async (email, name, phone, password) => {
    const response = await axios.post(`${API}/auth/signup`, { email, name, phone, password });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    return response.data;
  };

  /** Exchange a 4-digit POS PIN for a scoped POS token. */
  const posLogin = async (pin) => {
    const response = await axios.post(`${API}/pos/login`, { pin });
    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    // Synthetic user — the token has no linked users doc.
    const posUser = { id: 'pos-device', is_pos: true, is_admin: false, is_finance: false };
    setUser(posUser);
    return { token: response.data.token, user: posUser };
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, posLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
